'use client';

/**
 * EmptyStateWithAstor Component
 *
 * A rich empty state card that integrates with the Astor AI assistant.
 * Displays a contextual message from Astor and a "Start with Astor" CTA
 * that opens the AstorWidget with a prefilled message.
 *
 * Three variants:
 *   - card (default): Glass card wrapper
 *   - inline: No card wrapper, integrates into existing containers
 *   - fullpage: Centered full viewport
 *
 * Gracefully degrades when AstorContext is not available — the Astor message
 * bubble and "Start with Astor" button are simply hidden.
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useContext } from 'react';

import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';
import { AstorContext } from '../AstorContext/useAstorContext';
import { GlassButton } from '../GlassButton/GlassButton';

// ============================================================================
// TYPES
// ============================================================================

export interface EmptyStateWithAstorProps {
  /** Title text (use i18n key) */
  title: string;
  /** Description text (use i18n key) */
  description: string;
  /** Astor's contextual suggestion message */
  astorMessage?: string;
  /** Prefilled message when "Start with Astor" is clicked */
  astorPrefill?: string;
  /** Optional icon/illustration above the title */
  icon?: React.ReactNode;
  /** Primary CTA button label */
  primaryActionLabel?: string;
  /** Primary CTA click handler */
  onPrimaryAction?: () => void;
  /** Secondary actions (array of { label, onClick }) */
  secondaryActions?: Array<{ label: string; onClick: () => void }>;
  /** Variant: card (glass card), inline (no card wrapper), fullpage (centered full viewport) */
  variant?: 'card' | 'inline' | 'fullpage';
}

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' as const } },
} as const;

const bubbleVariants = {
  hidden: { opacity: 0, scale: 0.92, y: 8 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.35, ease: 'easeOut' as const, delay: 0.2 },
  },
} as const;

// ============================================================================
// COMPONENT
// ============================================================================

export function EmptyStateWithAstor({
  title,
  description,
  astorMessage,
  astorPrefill,
  icon,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActions,
  variant = 'card',
}: EmptyStateWithAstorProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const isDark = theme.palette.mode === 'dark';

  // Gracefully access Astor context (null when not in provider tree)
  const astorCtx = useContext(AstorContext);

  const hasAstor = astorCtx !== null && Boolean(astorMessage);

  const handleStartWithAstor = useCallback(() => {
    if (!astorCtx) return;
    astorCtx.openAstor('widget', astorPrefill);
  }, [astorCtx, astorPrefill]);

  // Glass theme tokens
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  // Wrapper styles per variant
  const wrapperSx =
    variant === 'fullpage'
      ? {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          width: '100%',
          px: 3,
        }
      : {};

  // Card styles (only for 'card' variant)
  const cardSx =
    variant === 'card'
      ? {
          background: glass.background,
          backdropFilter: glass.backdropBlur,
          WebkitBackdropFilter: glass.backdropBlur,
          border: `1px solid ${glass.border}`,
          borderRadius: 3,
          boxShadow: glass.shadow,
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: glass.shadowHover,
          },
        }
      : {};

  const content = (
    <motion.div
      variants={prefersReducedMotion ? undefined : containerVariants}
      initial={prefersReducedMotion ? undefined : 'hidden'}
      animate={prefersReducedMotion ? undefined : 'visible'}
      data-testid="empty-state-with-astor"
    >
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          py: variant === 'inline' ? 3 : 5,
          px: 4,
          textAlign: 'center',
          maxWidth: 520,
          mx: 'auto',
          ...cardSx,
        }}
      >
        {/* Icon / Illustration */}
        {icon && (
          <motion.div variants={prefersReducedMotion ? undefined : itemVariants}>
            <Box
              sx={{
                color: 'text.disabled',
                '& > svg': { fontSize: iconSizes['3xl'] },
              }}
            >
              {icon}
            </Box>
          </motion.div>
        )}

        {/* Title */}
        <motion.div variants={prefersReducedMotion ? undefined : itemVariants}>
          <Typography
            variant="h6"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
        </motion.div>

        {/* Description */}
        <motion.div variants={prefersReducedMotion ? undefined : itemVariants}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              maxWidth: 400,
            }}
          >
            {description}
          </Typography>
        </motion.div>

        {/* Astor Message Bubble */}
        <AnimatePresence>
          {hasAstor && (
            <motion.div
              variants={prefersReducedMotion ? undefined : bubbleVariants}
              initial={prefersReducedMotion ? undefined : 'hidden'}
              animate={prefersReducedMotion ? undefined : 'visible'}
              exit={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.92 }}
              style={{ width: '100%', maxWidth: 420 }}
            >
              <Box
                data-testid="astor-message-bubble"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  background: isDark
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.12)}`,
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    color: 'primary.main',
                    flexShrink: 0,
                  }}
                  data-testid="astor-avatar"
                >
                  <AutoAwesomeIcon sx={{ fontSize: iconSizes.md }} />
                </Avatar>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'left',
                    lineHeight: 1.6,
                  }}
                >
                  {astorMessage}
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <motion.div variants={prefersReducedMotion ? undefined : itemVariants}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              justifyContent: 'center',
              flexWrap: 'wrap',
              pt: 1,
            }}
          >
            {/* Start with Astor button */}
            {hasAstor && (
              <GlassButton
                variant="gradient"
                color="primary"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleStartWithAstor}
                data-testid="start-with-astor-button"
              >
                {t('astor.empty.startWithAstor')}
              </GlassButton>
            )}

            {/* Primary CTA */}
            {primaryActionLabel && onPrimaryAction && (
              <GlassButton
                variant={hasAstor ? 'outlined' : 'gradient'}
                color="primary"
                onClick={onPrimaryAction}
                data-testid="primary-action-button"
              >
                {primaryActionLabel}
              </GlassButton>
            )}

            {/* Secondary actions */}
            {secondaryActions?.map((action) => (
              <GlassButton
                key={action.label}
                variant="ghost"
                color="primary"
                onClick={action.onClick}
                data-testid={`secondary-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {action.label}
              </GlassButton>
            ))}
          </Stack>
        </motion.div>
      </Stack>
    </motion.div>
  );

  if (variant === 'fullpage') {
    return <Box sx={wrapperSx}>{content}</Box>;
  }

  return content;
}

EmptyStateWithAstor.displayName = 'EmptyStateWithAstor';

export default EmptyStateWithAstor;
